import {FC, useState} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Section} from '@/components/common/content/Section'
import {ChevronIcon} from '@/components/common/icons/ChevronIcon'
import {usePreferences} from '@/components/context/Preferences'
import {getRegionalCountryList} from '@/content/countryCards'
import {useHomepageContent} from '@/content/homepage'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {BrandSubscriptionPaymentFrequency} from '@/utils/brand-subscription/constants'
import {QueryLink} from '@/utils/constants'
import {Jurisdiction} from '@/utils/countries/constants'
import {getMinRegistrationPrice} from '@/utils/pricing'
import {
  getMinBrandMonitoringPriceByPaymentFrequency,
  getMinInfringementServicePrice,
} from '@/utils/pricing/brand-monitoring/calculations'
import {calculateTmRenewalJurisdictionPrice} from '@/utils/pricing/renewal/calculations'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'
import {getServicePrices} from '@/utils/supported-services/services'
import {tmRenewalJurisdictions} from '@/utils/trademark-renewal/constants'

import {CardListLayout} from '../../common/cards/CardListLayout'
import {BrandProtectionCard} from './BrandProtectionCard'

export type BrandProtectionCard = {
  key: string
  linkHref?: QueryLink | string
  labels: string[]
  title: string
  subtitle?: string
  description: string
  buttonText: string
  buttonVariant: ButtonVariant
  visibleOnMobile: boolean
  onClick?: () => void
  priceNote?: string
}

type BrandProtectionCardListProps = {
  title?: string
  list: BrandProtectionCard[]
  country?: Jurisdiction
  marginTop?: string
}

export const BrandProtectionCardList: FC<React.PropsWithChildren<BrandProtectionCardListProps>> = ({
  title,
  list,
  country,
  marginTop,
}) => {
  const {t} = useTranslation()

  const {cards} = useHomepageContent()
  const {origin, destination, currency} = usePreferences()
  const [isExpanded, setIsExpanded] = useState(false)
  const countryList = getRegionalCountryList(origin, destination)

  const getRenewalPrice = () => {
    const country = tmRenewalJurisdictions.includes(origin as unknown as Jurisdiction)
      ? origin
      : origin === 'GB'
      ? Jurisdiction.UK
      : Jurisdiction.US
    return calculateTmRenewalJurisdictionPrice(country as Jurisdiction, currency).total.vatBase
  }

  const getMinPrice = (key: string) => {
    const registrationPrice = getMinRegistrationPrice(currency, countryList, country)
    switch (key) {
      case 'registration':
        return registrationPrice
      case 'legal-service':
        return getServicePrices().Legal['tm-representation'][currency]
      case 'tm-renewal':
        return getRenewalPrice()
      case 'monitoring':
        return getMinBrandMonitoringPriceByPaymentFrequency(
          BrandSubscriptionPaymentFrequency.monthly,
          currency,
        )
      case 'infringement':
        return getMinInfringementServicePrice(currency)
      default:
        return registrationPrice
    }
  }

  return (
    <>
      <Section hasPaddingTop marginTop={marginTop}>
        <CardListLayout title={title || t(cards.title)} titleMaxWidth="100%">
          {list.map(
            ({
              key,
              linkHref,
              labels,
              title,
              subtitle,
              description,
              buttonText,
              buttonVariant,
              visibleOnMobile,
              onClick,
              priceNote,
            }) => (
              <BrandProtectionCard
                key={key}
                link={linkHref}
                labels={labels}
                title={title}
                subtitle={subtitle}
                description={description}
                price={getMinPrice(key)}
                priceNote={priceNote}
                buttonText={buttonText}
                buttonVariant={buttonVariant}
                isHidden={!visibleOnMobile && !isExpanded}
                isExpanded={isExpanded}
                onClick={onClick}
              />
            ),
          )}
        </CardListLayout>
        {!isExpanded && (
          <div className="button-wrapper">
            <Button
              icon={<ChevronIcon color={theme.palette.primary.main} />}
              onClick={() => setIsExpanded(true)}
              type="button"
            >
              {t(cards.showMoreButtonText)}
            </Button>
          </div>
        )}
      </Section>

      <style jsx>{`
        .button-wrapper {
          display: none;
        }

        @media ${MOBILE_SCREENSIZE} {
          .button-wrapper {
            display: block;
            text-align: center;
            margin-top: ${theme.spacing[5]};
          }
        }
      `}</style>
    </>
  )
}
